import { useState, useEffect } from "react";
import {
    TextField,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const RANDOM_INPUT_OPTIONS = [
    "${PHONE_RANDOM_DATA}",
    "${EMAIL_RANDOM_DATA}",
    "${NAME_RANDOM_DATA}",
    "${TEXT_RANDOM_DATA}",
    "${XSS_INJECTION}",
    "${NUMBER_RANDOM_DATA}",
    "${INVALID_EMAIL_RANDOM_DATA}",
    "${INVALID_PHONE_RANDOM_DATA}",
    "${INVALID_NAME_RANDOM_DATA}",
];

const RANDOM_DROPDOWN_OPTIONS = ["${RANDOM_DROPDOWN_DATA}"];
const RANDOM_UPLOAD_OPTIONS = [
    "${VALID_CSV_FILE}",
    "${INVALID_CSV_FILE}",
    "${VALID_PDF_FILE}",
];

export default function EditableInput(props) {
    const {
        value,
        type = "text",
        onSave,
        disabled = false,
        isSelect = false, // distinguish dropdown steps
        showRandomOptions = true,
        isUpload = false,
        ...rest
    } = props;

    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const [originalValue, setOriginalValue] = useState(value);
    const [error, setError] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        if (!isEditing) {
            setCurrentValue(value);
            setOriginalValue(value);
        }
    }, [value, isEditing]);

    const handleEdit = () => {
        if (isUpload) return;
        closeMenu();
        setIsEditing(true);
        setError(false);
        setOriginalValue(currentValue);
    };

    const handleCancel = () => {
        closeMenu();
        setCurrentValue(originalValue);
        setIsEditing(false);
        setError(false);
    };

    const handleSave = () => {
        closeMenu();
        onSave(currentValue);
        setOriginalValue(currentValue);
        setIsEditing(false);
        setError(false);
    };

    const openMenu = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const closeMenu = () => setAnchorEl(null);

    const handleSelectRandom = (val) => {
        closeMenu();
        onSave(val); // autosave
    };

    const getRandomOptions = () => {
        if (isUpload) return RANDOM_UPLOAD_OPTIONS;
        if (isSelect) return RANDOM_DROPDOWN_OPTIONS;
        return RANDOM_INPUT_OPTIONS;
    };

    const randomOptions = showRandomOptions ? getRandomOptions() : [];

    return (
        <TextField
            className="stepsAccordionTableBodyCellEditableInput"
            fullWidth
            type={type}
            value={currentValue}
            disabled={!isEditing || disabled}
            onChange={(e) => {
                setCurrentValue(e.target.value);
                if (error) setError(false);
            }}
            error={error}
            helperText={error ? "Value cannot be empty" : ""}
            sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#2D4356",
                },
            }}
            InputProps={{
                endAdornment: (
                    <InputAdornment
                        position="end"
                        sx={{ display: "flex", alignItems: "center" }}
                    >
                        {showRandomOptions && !isEditing ? (
                            <>
                                <Tooltip title="Random data options">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={openMenu}
                                            disabled={disabled}
                                        >
                                            <ArrowDropDownIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={closeMenu}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "right",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                >
                                    {randomOptions.map((opt) => (
                                        <MenuItem
                                            key={opt}
                                            onClick={() =>
                                                handleSelectRandom(opt)
                                            }
                                        >
                                            {opt}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        ) : null}
                        {isEditing ? (
                            <>
                                <IconButton
                                    onClick={handleSave}
                                    edge="end"
                                    color="success"
                                    sx={{ mr: 0 }}
                                >
                                    <CheckIcon />
                                </IconButton>
                                <IconButton
                                    onClick={handleCancel}
                                    edge="end"
                                    color="error"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </>
                        ) : (
                            <IconButton
                                onClick={handleEdit}
                                edge="end"
                                color="primary"
                                disabled={disabled}
                            >
                                <EditOutlinedIcon />
                            </IconButton>
                        )}
                    </InputAdornment>
                ),
            }}
            {...rest}
        />
    );
}
